// src/pages/CartPage.js
import React, { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { LanguageContext } from "../context/LanguageContext";
import CartItem from "../components/CartItem";
import { useCart } from "../context/CartContext";
import { apiRequest } from "../services/api";

function CartPage() {
  const { lang } = useContext(LanguageContext);
  const navigate = useNavigate();
  const { cartItems, subtotal } = useCart();

  const isEmpty = cartItems.length === 0;

  const [shipping, setShipping] = useState({
    fee: 4,
    free_enabled: false,
    free_threshold: 0,
  });

  const hasOutOfStock = cartItems.some((item) => item.stock_qty === 0);

  const hasQtyExceedStock = cartItems.some(
    (item) => item.stock_qty > 0 && item.qty > item.stock_qty
  );

  const disableCheckout = hasOutOfStock || hasQtyExceedStock;

  const TEXT = {
    EN: {
      title: "YOUR CART",
      empty: "Your cart is empty.",
      continue: "Continue Shopping",
      wishlist: "View Wishlist",
      subtotal: "Subtotal:",
      total: "Total:",
      checkout: "PROCEED TO CHECKOUT",
      deliveryInfo: "Delivery fees",
      freeDeliveryInfo: "Free delivery on orders above",
      pickupInfo: "Pick up from store",
    },
    AR: {
      title: "سلة المشتريات",
      empty: "سلة المشتريات فارغة.",
      continue: "متابعة التسوق",
      wishlist: "عرض المفضلة",
      subtotal: "المجموع:",
      total: "الإجمالي:",
      checkout: "إتمام الشراء",
      deliveryInfo: "رسوم التوصيل",
      freeDeliveryInfo: "توصيل مجاني للطلبات فوق",
      pickupInfo: "استلام من المحل",
    },
  };

  const t = TEXT[lang];

  useEffect(() => {
    apiRequest("/shipping-settings") // ✅ PUBLIC endpoint
      .then((s) =>
        setShipping({
          fee: Number(s.default_fee ?? 4),
          free_enabled: !!s.free_enabled,
          free_threshold: Number(s.free_threshold ?? 0),
        })
      )
      .catch(() => {});
  }, []);

  // ✅ Cart page total is now same as subtotal (no delivery calc here)
  const finalTotal = Number(subtotal) || 0;

  return (
    <div
      className="w-full min-h-screen bg-white pt-[95px] px-4 pb-24"
      dir={lang === "AR" ? "rtl" : "ltr"}
    >
      {/* BACK BUTTON */}
      <button
        onClick={() => navigate(-1)}
        className="
          absolute top-[80px] left-4 
          text-3xl text-[#2E3135] opacity-60 
          hover:opacity-100 transition
        "
      >
        ×
      </button>

      {/* TITLE */}
      <h1 className="text-xl font-semibold text-[#2E3135] text-center tracking-wide">
        {t.title}
      </h1>
      <div className="h-[2px] w-12 bg-black mt-2 mb-6 mx-auto"></div>

      {/* EMPTY STATE */}
      {isEmpty && (
        <div className="flex flex-col items-center justify-center mt-20">
          <div
            className="
              w-20 h-20 border border-[#E6D8D2] rounded-full 
              flex items-center justify-center
            "
          >
            <img src="/icons/bag.svg" className="w-9 opacity-50" alt="" />
          </div>

          <p className="text-[#7A7674] text-sm mt-4">{t.empty}</p>

          <button
            onClick={() => navigate("/")}
            className="
              mt-6 bg-black text-white px-6 py-3 rounded-md 
              font-semibold tracking-wide text-sm hover:bg-[#222] transition
            "
          >
            {t.continue}
          </button>

          <button
            onClick={() => navigate("/wishlist")}
            className="
              mt-3 px-6 py-3 rounded-md font-semibold text-sm
              border border-[#E6D8D2] text-[#2E3135]
              bg-white hover:bg-[#F7F3F1] transition
            "
          >
            {t.wishlist}
          </button>
        </div>
      )}

      {/* CART ITEMS */}
      {!isEmpty && (
        <div className="flex flex-col gap-4">
          {cartItems.map((item) => (
            <CartItem key={item.id} item={item} lang={lang} />
          ))}
        </div>
      )}

      {hasOutOfStock && (
        <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-300 text-red-700 text-sm text-center">
          {lang === "EN"
            ? "Some items in your cart are out of stock. Please remove them to continue checkout."
            : "بعض المنتجات في سلة المشتريات نفدت من المخزون. يرجى حذفها للمتابعة."}
        </div>
      )}

      {hasQtyExceedStock && (
        <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-300 text-red-700 text-sm text-center">
          {lang === "EN"
            ? "Some items exceed available stock. Please reduce quantity or remove them to continue checkout."
            : "بعض المنتجات الكمية المطلوبة أكبر من المتوفر. يرجى تقليل الكمية أو حذف المنتج للمتابعة."}
        </div>
      )}

      {/* SUMMARY */}
      {!isEmpty && (
        <div
          className="
            bg-white border border-[#E6D8D2] p-5 rounded-lg mt-8 
            shadow-[0_0_10px_rgba(0,0,0,0.05)]
          "
        >
          {/* Subtotal */}
          <div className="flex justify-between text-sm mb-3">
            <span className="text-[#2E3135] font-medium">{t.subtotal}</span>
            <span className="font-semibold">${Number(subtotal).toFixed(2)}</span>
          </div>

          {/* INFO LINES (instead of delivery line) */}
          <div className="text-xs text-[#7A7674] space-y-1 mb-3">
            <p>
              • {t.deliveryInfo}: ${Number(shipping.fee || 0).toFixed(2)}
            </p>

            {shipping.free_enabled && Number(shipping.free_threshold) > 0 && (
              <p>
                • {t.freeDeliveryInfo}: ${Number(shipping.free_threshold).toFixed(2)}
              </p>
            )}

            <p>
              • {t.pickupInfo}: $0.00
            </p>
          </div>

          <hr className="my-4 border-[#E6D8D2]" />

          {/* Total = subtotal */}
          <div className="flex justify-between text-lg font-bold text-[#2E3135]">
            <span>{t.total}</span>
            <span>${finalTotal.toFixed(2)}</span>
          </div>

          <button
            onClick={() => {
  if (disableCheckout) return;

  apiRequest("/analytics/track", {
    method: "POST",
    body: JSON.stringify({
      action_type: "enter_checkout",
      entity_type: "cart",
    }),
  }).catch(() => {});

  if (typeof window.fbq === "function") {
    window.fbq("track", "InitiateCheckout", {
      value: Number(finalTotal) || 0,
      currency: "USD",
      num_items: cartItems.reduce((sum, it) => sum + (Number(it.qty) || 0), 0),
    });
  }

  navigate("/checkout");
}}
            disabled={disableCheckout}
            className={`
              w-full mt-5 py-3 rounded-md text-lg font-semibold transition
              ${
                disableCheckout
                  ? "bg-gray-400 cursor-not-allowed text-white"
                  : "bg-black text-white hover:bg-[#222] active:scale-95"
              }
            `}
          >
            {t.checkout}
          </button>

          <button
            onClick={() => navigate("/wishlist")}
            className="
              w-full mt-3 py-3 rounded-md font-semibold text-sm
              border border-[#E6D8D2] text-[#2E3135]
              bg-white hover:bg-[#F7F3F1] transition
            "
          >
            {t.wishlist}
          </button>
        </div>
      )}
    </div>
  );
}

export default CartPage;
